import { InfoCircle } from 'iconoir-react';
import React, { Suspense, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useMedia } from 'react-use';
import { useTheme } from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import { Checkbox } from '@savgroup-front-common/core/atoms/checkbox';
import { RadioButton } from '@savgroup-front-common/core/atoms/Form/Radio';
import { Link } from '@savgroup-front-common/core/atoms/link';
import { ProductImage } from '@savgroup-front-common/core/atoms/ProductImage';
import { Skeleton } from '@savgroup-front-common/core/atoms/Skeleton';
import { SafeFormattedMessageWithoutSpread } from '@savgroup-front-common/core/formatters';
import { ArrowNakedRightIcon } from '@savgroup-front-common/core/protons/icons/ArrowNaked/ArrowNakedRight.icon';
import {
  FileShortSummary,
  OrderDetailsProduct,
  OrderDetailsProductWithOpenedClaim,
  OrderLineType,
} from '@savgroup-front-common/types';

import { useHasForceOneProductPerClaim } from '../../../../../app/hooks/useHasForceOneProductPerClaim';
import { ChoiceProductValues } from '../../NewChooseProductPage.types';
import ChooseProductTag from '../ChooseProductTag/ChooseProductTag';

import useChooseProductCard from './ChooseProductCard.hooks';
import {
  $ChooseProductCardCheckboxContainer,
  $ChooseProductCardContainer,
  $ChooseProductCardImageContainer,
  $ChooseProductCardInfoContainer,
  $ColonProductInfo,
  $GiftProductMessage,
  $ModelDisplayName,
  $OpenFile,
  $RowProductInfo,
} from './ChooseProductCard.styles';
import { FinalizeClaimGroup } from './FinalizeClaimGroup/FinalizeClaimGroup';
import messages from './messages';

const SINGLE_PRODUCT_RADIO_NAME = 'selectedOwnerProductId';

interface ChooseProductCardProps {
  product: OrderDetailsProduct;
  openedFiles: FileShortSummary[];
  isDisabled?: boolean;
  isInProgress?: boolean;
  notEligible?: boolean;
  dataTestId?: string;
}

const ChooseProductCard: React.FC<ChooseProductCardProps> = ({
  product,
  openedFiles,
  isDisabled = false,
  isInProgress = false,
  notEligible = false,
  dataTestId,
}) => {
  const theme = useTheme();
  const isMobileView = useMedia(media.maxWidth.xs);
  const { modelDetail } = useChooseProductCard({ product });
  const hasForceOneProductPerClaim = useHasForceOneProductPerClaim();
  const formContext = useFormContext<ChoiceProductValues>();

  const { control, watch } = formContext;

  const selectedOwnerProductIds = watch('selectedOwnerProductIds');

  const isChecked = selectedOwnerProductIds?.includes(product.ownerProductId);

  const handleSelectSingleProduct = useCallback(
    (onChange: (value: string[]) => void) => {
      onChange([product.ownerProductId]);
    },
    [product.ownerProductId],
  );

  const isOpenedFile = openedFiles.some((file) => {
    return file.fileProducts.some(
      ({ ownerProductId }) => ownerProductId === product.ownerProductId,
    );
  });

  const fileOpenedId = openedFiles.find((file) => {
    return file.fileProducts.filter(
      ({ ownerProductId }) => ownerProductId === product.ownerProductId,
    );
  });

  return (
    <>
      {!isDisabled && (
        <Controller
          control={control}
          name="selectedOwnerProductIds"
          render={({ field }) => {
            return (
              <$ChooseProductCardContainer
                isDisabled={false}
                isChecked={isChecked}
                data-testid={dataTestId}
                for={product.ownerProductId}
              >
                <$RowProductInfo>
                  <$ChooseProductCardCheckboxContainer isChecked={isChecked}>
                    {hasForceOneProductPerClaim && (
                      <RadioButton
                        name={SINGLE_PRODUCT_RADIO_NAME}
                        id={product.ownerProductId}
                        value={product.ownerProductId}
                        checked={isChecked}
                        dataTestId={`selectProduct-${product.ownerProductId}`}
                        onChange={() =>
                          handleSelectSingleProduct(field.onChange)
                        }
                        isCompact
                      />
                    )}
                    {!hasForceOneProductPerClaim && (
                      <Checkbox
                        name={product.ownerProductId}
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => {
                          if (isChecked) {
                            return field.onChange(
                              selectedOwnerProductIds.filter(
                                (item) => item !== product.ownerProductId,
                              ),
                            );
                          } else {
                            field.onChange([
                              ...selectedOwnerProductIds,
                              product.ownerProductId,
                            ]);
                          }

                          return undefined;
                        }}
                      />
                    )}
                  </$ChooseProductCardCheckboxContainer>

                  <$ChooseProductCardImageContainer isDisabled={isDisabled}>
                    <ProductImage pictureUrl={modelDetail?.pictureUrl} />
                  </$ChooseProductCardImageContainer>
                  <$ChooseProductCardInfoContainer isDisabled={isDisabled}>
                    <>
                      {isMobileView && (
                        <ChooseProductTag
                          product={product}
                          openedFiles={openedFiles}
                          isSmall={isMobileView}
                        />
                      )}
                      <$ModelDisplayName>
                        {product.modelDisplayName}
                      </$ModelDisplayName>
                      {product.orderLineType === OrderLineType.GIFT && (
                        <$GiftProductMessage>
                          <InfoCircle width={16} height={16} />
                          <SafeFormattedMessageWithoutSpread
                            message={messages.giftProduct}
                          />
                        </$GiftProductMessage>
                      )}
                    </>
                  </$ChooseProductCardInfoContainer>
                  {!isMobileView && (
                    <ChooseProductTag
                      product={product}
                      openedFiles={openedFiles}
                    />
                  )}
                </$RowProductInfo>
              </$ChooseProductCardContainer>
            );
          }}
        />
      )}

      {isDisabled && (
        <$ChooseProductCardContainer
          isDisabled
          isInProgress={isInProgress}
          data-testid={dataTestId}
        >
          <$ColonProductInfo>
            <$RowProductInfo>
              <$ChooseProductCardImageContainer isDisabled={isDisabled}>
                <ProductImage pictureUrl={modelDetail?.pictureUrl} />
              </$ChooseProductCardImageContainer>
              <$ChooseProductCardInfoContainer isDisabled>
                <>
                  {isMobileView && (
                    <ChooseProductTag
                      product={product}
                      openedFiles={openedFiles}
                      isInProgress={isInProgress}
                      notEligible={notEligible}
                      isSmall={isMobileView}
                    />
                  )}

                  <$ModelDisplayName>
                    {product.modelDisplayName}
                  </$ModelDisplayName>
                  {product.orderLineType === OrderLineType.GIFT && (
                    <$GiftProductMessage>
                      <InfoCircle width={16} height={16} />
                      <SafeFormattedMessageWithoutSpread
                        message={messages.giftProduct}
                      />
                    </$GiftProductMessage>
                  )}
                  {isMobileView && isOpenedFile && fileOpenedId?.fileId && (
                    <$OpenFile>
                      <Link
                        href={`/new_design/files/${fileOpenedId?.fileId}`}
                        icon={
                          <ArrowNakedRightIcon
                            color={theme.colors.black}
                            size="14px"
                          />
                        }
                      >
                        <SafeFormattedMessageWithoutSpread
                          message={messages.fileOpenedLink}
                        />
                      </Link>
                    </$OpenFile>
                  )}

                  {isMobileView && product.claimGroupId && (
                    <Suspense fallback={<Skeleton width="100px" />}>
                      <FinalizeClaimGroup
                        product={product as OrderDetailsProductWithOpenedClaim}
                      />
                    </Suspense>
                  )}
                </>
              </$ChooseProductCardInfoContainer>
              {!isMobileView && (
                <div>
                  <ChooseProductTag
                    product={product}
                    openedFiles={openedFiles}
                    isInProgress={isInProgress}
                    notEligible={notEligible}
                  />

                  {isOpenedFile && fileOpenedId?.fileId && (
                    <$OpenFile>
                      <Link
                        href={`/new_design/files/${fileOpenedId?.fileId}`}
                        icon={
                          <ArrowNakedRightIcon
                            color={theme.colors.black}
                            size="14px"
                          />
                        }
                      >
                        <SafeFormattedMessageWithoutSpread
                          message={messages.fileOpenedLink}
                        />
                      </Link>
                    </$OpenFile>
                  )}

                  {product.claimGroupId && (
                    <Suspense fallback={<Skeleton width="100px" />}>
                      <FinalizeClaimGroup
                        product={product as OrderDetailsProductWithOpenedClaim}
                      />
                    </Suspense>
                  )}
                </div>
              )}
            </$RowProductInfo>
          </$ColonProductInfo>
        </$ChooseProductCardContainer>
      )}
    </>
  );
};

export default ChooseProductCard;
